<template>
  <div class="landing">
    <!-- NAVBAR -->
    <header class="nav">
      <div class="nav__inner">
        <div class="nav__brand">
          <img
            class="logo-image"
            src="/assets/landing/research-ai-logo.svg"
            alt="ResearchAI logo"
          />
        </div>

        <nav class="nav__links" aria-label="Primary">
          <a href="#how-it-works" class="nav__link">How it works</a>
          <a href="#who-its-for" class="nav__link">Who it’s for</a>
          <a href="#why-trust" class="nav__link">Why we are trusted</a>
          <router-link to="/library" class="nav__link"> Library </router-link>
        </nav>
        <div class="nav__actions" v-if="!$pigeonline.userIsAuthenticated()">
          <button @click="openRegisterModal" class="landing_btn-gradient">Sign up for free</button>
          <router-link to="/login" class="landing_btn-outline"> Log in </router-link>
        </div>
        <div class="nav__actions" v-if="$pigeonline.userIsAuthenticated()">
          <router-link to="/login" class="landing_btn-outline"> Return to app </router-link>
        </div>
      </div>
    </header>
    <RegisterSmallModal
      :status="isRegisterSmallExpanded"
      @close="isRegisterSmallExpanded = false"
      @registered="handleSmallRegister"
    />

    <main>
      <!-- HERO -->
      <section class="hero">
        <div class="hero__inner hero__inner--narrow">
          <div class="hero__content">
            <p class="hero__eyebrow">Validation‑first AI for analyzing text</p>
            <!-- <h1 class="hero__title">
              Tracking of citizen voice or client and employee feedback, that you can trust.
            </h1> -->
            <!-- <h1 class="hero__title">
              Real-time feedback intelligence that you can trust.
            </h1> -->
            <!-- <h1 class="hero__title">
              Structured insights from unstructured text.
            </h1> -->
            <h1 class="hero__title">
              Make every voice count,<br />
              without compromising privacy or accuracy.
            </h1>
            <p class="larger-font centered">
              Turn thousands of comments into defensible, privacy‑first insights.
            </p>

            <div class="hero__badges mt-3rem centered">
              <span class="badge">Private</span>
              <span class="badge">Reproducible</span>
              <span class="badge">Trusted by governments</span>
            </div>

            <div class="nav__actions centered">
              <button @click="openRegisterModal" class="landing-btn landing_btn-gradient">
                Start finding text insights
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- USE CASES -->
      <section class="section">
        <div class="section__inner">
          <header class="section__header">
            <h2 class="section__centred">What you can do with ResearchAI</h2>
            <p class="section__centred">
              A few focused projects per month can unlock disproportionate value across your
              services, products, and policies.
            </p>
          </header>

          <div class="grid grid--cards grid--cards--whoFor">
            <div
              class="landing_card landing_card--tile"
              v-for="useCase in useCases"
              :key="useCase.title"
            >
              <img class="who-card__image" :src="useCase.img" :alt="useCase.title" />
              <h3 class="landing_card__title">{{ useCase.title }}</h3>
              <p class="landing_card__text">{{ useCase.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- SOLUTION: ARCHITECTURE -->
      <section class="section section--light">
        <div class="section__inner">
          <header class="section__header">
            <h2 class="section__centred">Privacy- and accuracy-first AI</h2>
            <p class="section__centred">
              ResearchAI combines deterministic methods, analyst‑trained models, and constrained
              GenAI into a single workflow that keeps humans in control.
            </p>
          </header>

          <div class="grid grid--three">
            <div class="landing_card">
              <h3 class="landing_card__title">Deterministic analysis</h3>
              <ul class="list list--tight">
                <li>Keyword and lemma matching.</li>
                <li>Token similarity and pattern detection.</li>
                <li>Transparent, reproducible, and auditable.</li>
              </ul>
            </div>
            <div class="landing_card">
              <h3 class="landing_card__title">Private ML trained by your analysts</h3>
              <ul class="list list--tight">
                <li>Logistic regression over analyst decisions.</li>
                <li>Models are visible, editable, and retrainable.</li>
                <li>Delete a model and it’s gone—no third‑party access.</li>
              </ul>
            </div>
            <div class="landing_card">
              <h3 class="landing_card__title">Strategic GenAI (optional)</h3>
              <ul class="list list--tight">
                <li>Summaries only after validation.</li>
                <li>No unsupervised theme discovery.</li>
                <li>No hallucinated evidence or hidden inference.</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="workslop-trust-panel">
          <p class="workslop-trust-text">
            <strong>Your data stays private, encrypted, and fully under your control.</strong><br />
            ResearchAI follows industry‑leading security and privacy standards, including SOC 2 Type
            II, GDPR, PIPEDA, and UK Cyber Security Essentials. We support the right to be
            forgotten, the right to access your data, and offer on‑premise deployment for
            organizations with strict sovereignty requirements.
          </p>
          <div>
            <!-- Insert your 3 logos here -->
            <img class="circle_logos" src="/assets/landing/SOC2_logo.png" alt="SOC 2 Type II" />
            <img class="circle_logos" src="/assets/landing/GDPR_logo.png" alt="GDPR" />
            <img
              class="circle_logos"
              src="/assets/landing/cyber-essentials_logo.png"
              alt="Cyber Essentials"
            />
          </div>
        </div>
        <div class="flexbox mt-2rem">
          <button
            @click="openRegisterModal"
            class="landing-btn landing_btn-gradient flexbox centered"
          >
            Get started
          </button>
        </div>
      </section>

      <!-- WHO IT'S FOR -->
      <section id="who-its-for" class="section section--light">
        <div class="section__inner">
          <header class="section__header">
            <h2 class="section__centred">Built for organizations with real feedback data</h2>
            <p class="section__centred">
              If your organization receives thousands of comments, complaints, survey responses, or
              open‑text feedback, ResearchAI turns that into measurable, defensible insight.
            </p>
          </header>

          <div class="grid grid--cards grid--cards--whoFor">
            <div class="who-card" v-for="item in whoFor" :key="item.title">
              <img class="who-card__image" :src="item.img" :alt="item.title" />

              <h3 class="who-card__title">{{ item.title }}</h3>
              <p class="who-card__text">{{ item.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- TESTIMONY -->
      <section id="testimony" class="section">
        <!-- PARTNER LOGO STRIP -->
        <section id="partners" class="partners">
          <h3 class="partners__heading">10 years of trusted research partnerships</h3>

          <div class="partners__logo-row">
            <img
              src="/assets/landing/clients/servicecanada_logo.png"
              alt="ESDC / Service Canada"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/lse_logo.png"
              alt="London School of Economics"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/ut_austin_logo.png"
              alt="University of Texas Austin"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/uae_pmo_logo.png"
              alt="PMO of UAE"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/yukon_logo.png"
              alt="Government of Yukon"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/peel_logo.png"
              alt="Region of Peel"
              class="partners__logo"
            />
            <img src="/assets/landing/clients/cric_logo.png" alt="CRIC" class="partners__logo" />
            <img
              src="/assets/landing/clients/signal49_logo.png"
              alt="Signal49 Research"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/bank_of_canada_logo.png"
              alt="Bank of Canada"
              class="partners__logo"
            />
            <img
              src="/assets/landing/clients/nasdaq_logo.png"
              alt="NASDAQ"
              class="partners__logo"
            />
          </div>
        </section>

        <div class="section__inner">
          <div class="landing_quote">
            <p class="landing_quote__eyebrow">Testimony</p>

            <blockquote class="landing_quote__text">
              “We upload thousands of citizen comments each month and turn them into validated,
              defensible themes using our own dictionaries and models. ResearchAI has become a core
              part of how we track service pain‑points, measure change, and make real‑time
              decisions. It’s the first time we’ve been able to quantify real experiences in a
              scalable, trusted way.”
            </blockquote>

            <p class="landing_quote__attribution">
              — Senior Data Strategist, Government Service Team
            </p>
          </div>
        </div>
      </section>

      <!-- PROBLEM: WORKSLOP -->
      <section class="section">
        <header class="section__header">
          <h2 class="section__centred">Why GPTs fail when accuracy matters</h2>
        </header>

        <div class="section__inner section__inner--split">
          <div class="section__block" style="width: 50%">
            <p class="section__intro">
              General‑purpose chatbots are powerful, but they are not designed for accountable,
              repeatable analysis of citizen, client, or employee feedback.
            </p>

            <div class="problem-tags">
              <span class="problem-tag">Non‑deterministic outputs</span>
              <span class="problem-tag">Hallucinations</span>
              <span class="problem-tag">No audit trail</span>
              <span class="problem-tag">No replicability</span>
              <span class="problem-tag">Weak signals get lost</span>
              <span class="problem-tag">Not defensible in public sector</span>
            </div>
          </div>

          <div class="section__block">
            <div class="landing_quote">
              <p class="landing_card__stat">“The Workslop problem”</p>
              <p class="landing_card__text">
                Low‑quality, unverifiable AI output that looks polished but cannot be trusted.
              </p>
              <p class="landing_card__text">
                ResearchAI is not a chatbot. It is a validation‑first analytics platform for
                structured feedback datasets. Your analysts get a methodology that stands up to
                scrutiny from executives, auditors, and the public.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- WORKFLOW -->
      <section id="how-it-works" class="section">
        <div class="section__inner">
          <header class="section__header">
            <h2 class="section__centred">How it works</h2>
            <p class="section__centred">
              A simple workflow that turns unused text into a repeatable measurement system.
            </p>
          </header>

          <div class="grid grid--three">
            <div class="landing_card">
              <h3 class="landing_card__title">
                1. Upload spreadsheets and start your private project.
              </h3>
              <p>
                Import spreadsheets with surveys, complaints, social media responses, tickets, etc.
              </p>
              <img
                class="who-card__image"
                src="/assets/landing/landing_step-upload.jpg"
                alt="Upload step"
              />
            </div>
            <div class="landing_card">
              <h3 class="landing_card__title">2. Choose, build and validate your model.</h3>
              <p>
                Generate themes from existing models or build your own by tagging or correcting
                matches.
              </p>
              <img
                class="who-card__image"
                src="/assets/landing/landing_step-validate.jpg"
                alt="Choose models step"
              />
            </div>
            <!-- <div class="landing_card">
              <h3 class="landing_card__title">Validate themes and run analysis</h3>
              <p>
                Analysts review, adjust, and approve themes—creating a transparent, reusable measurement model.
              </p>
              <img
                class="who-card__image"
                src="/assets/landing/landing_step-validate.jpg"
                alt="Choose models step"
              />
            </div> -->
            <div class="landing_card">
              <h3 class="landing_card__title">3. Export insights and track change over time.</h3>
              <p>
                You can now track who has said what, how often, how it changes over time, and how it
                impacts sentiment. Export to excel, PowerBI, or hook into your APIs.
              </p>
              <img
                class="who-card__image"
                src="/assets/landing/landing_step-themes.jpg"
                alt="Upload step"
              />
            </div>
          </div>

          <div class="flexbox mt-2rem">
            <button
              @click="openRegisterModal"
              class="landing-btn landing_btn-gradient flexbox centered"
            >
              Get started
            </button>
          </div>
        </div>
      </section>

      <!-- THOUGHT LEADERSHIP -->
      <section id="thought-leadership" class="section section--light">
        <div class="section__inner">
          <header class="section__header">
            <h2 class="section__centred">Thought leadership in validation‑first AI</h2>
            <p class="section__centred">
              ResearchAI is part platform, part methodology. We actively shape how governments and
              enterprises think about trustworthy AI and high‑stakes text analysis.
            </p>
          </header>

          <div class="grid grid--cards">
            <div
              class="landing_card landing_card--tile"
              v-for="item in thoughtLeadership"
              :key="item.title"
            >
              <a
                v-if="item.link"
                :href="item.link"
                target="_blank"
                rel="noopener noreferrer"
                class="landing_card__link"
              >
                <img class="who-card__image-long" :src="item.img" :alt="item.title" />
                <p class="landing_card__eyebrow">{{ item.tag }}</p>
                <h3 class="landing_card__title">{{ item.title }}</h3>
                <p class="landing_card__text">{{ item.text }}</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      <!-- SIGNUP FUNNEL -->
      <section class="section section--accent">
        <div class="section__inner section__inner--center landing_card landing_card__thin">
          <h2>Create an account to start analyzing your feedback data</h2>
          <p>
            Share your work email and a brief description of your work so we can tailor your
            experience and best support you.
          </p>
          <div class="register-small__org-promo">
            For organizations: If your use case fits, we’ll activate a 30‑day enterprise trial and
            guide you through your first project.
          </div>
          <RegisterSmall @registered="handleSmallRegister" />
        </div>
      </section>
    </main>
    <BookADemo :status="isBookADemoExpanded" @close="isBookADemoExpanded = false"></BookADemo>
    <footer class="footer-rai">
      <div class="footer-rai__inner">
        <!-- CONTACT BLOCK -->
        <div class="footer-rai__contact">
          <h3 class="footer-rai__heading">Contact us</h3>

          <div class="footer-rai__contact-item">
            <img src="/assets/landing/mail_icon.png" alt="Email icon" class="footer-rai__icon" />
            <div>
              <div class="footer-rai__label">Email</div>
              <div class="footer-rai__value">hello@researchai.io</div>
            </div>
          </div>

          <a
            href="https://www.linkedin.com/company/25166569/"
            target="_blank"
            class="footer-rai__contact-item footer-rai__link-item"
          >
            <img src="/assets/landing/linkedin.png" alt="LinkedIn icon" class="footer-rai__icon" />
            <div>
              <div class="footer-rai__label">LinkedIn</div>
              <div class="footer-rai__value footer-rai__value--link">Follow us!</div>
            </div>
          </a>
        </div>

        <!-- BOOK A DEMO -->
        <div
          class="footer-rai__contact-item footer-rai__link-item cursor-pointer"
          @click="isBookADemoExpanded = true"
        >
          <SvgIconDecorative class="svg-icon" icon="calendar" id="clean-icon" />
          <div>
            <div class="footer-rai__label">Book a demo</div>
            <div class="footer-rai__value footer-rai__value--link">Schedule a walkthrough</div>
          </div>
        </div>

        <!-- POLICY LINKS -->
        <div class="footer-rai__policies">
          <div class="footer-rai__policy-links">
            <router-link to="/library/privacy-policy" class="footer-rai__policy-link">
              Privacy Policy
            </router-link>
            <span class="footer-rai__divider">|</span>
            <router-link to="/library/ethics-policy" class="footer-rai__policy-link">
              Ethics Policy
            </router-link>
            <span class="footer-rai__divider">|</span>
            <router-link to="/library/terms" class="footer-rai__policy-link">
              Terms of Use
            </router-link>
          </div>
          <div class="footer-rai__copyright">© 2026 PigeonLine Inc.</div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import RegisterSmallModal from '@/components/Register/RegisterSmallModal.vue';
import RegisterSmall from '@/components/Register/RegisterSmall.vue';
import BookADemo from '@/components/ContactUs/BookADemo.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';
export default {
  name: 'LandingPage',
  components: {
    RegisterSmallModal,
    RegisterSmall,
    BookADemo,
    SvgIconDecorative,
  },
  data() {
    return {
      isRegisterSmallExpanded: false,
      isBookADemoExpanded: false,

      whoFor: [
        {
          title: 'Government service teams',
          text: 'Analyze citizen feedback on services, programs, and channels with defensible, repeatable methods.',
          img: '/assets/landing/govtech_landing-card.jpg',
        },
        {
          title: 'CX / UX and research teams',
          text: 'Turn open‑ended survey responses into structured metrics that track over time.',
          img: '/assets/landing/ux_landing-card.jpg',
        },
        {
          title: 'Policy and program evaluation',
          text: 'Measure the impact of policy or service changes on real‑world experiences and perceptions.',
          img: '/assets/landing/policy_landing-card.jpg',
        },
        {
          title: 'Employee experience and HR',
          text: 'Quantify themes and sentiment in employee surveys, exit interviews, and internal feedback.',
          img: '/assets/landing/employee_landing-card.jpg',
        },
        {
          title: 'Complaint and case management',
          text: 'Detect emerging issues early and prioritize systemic problems, not just individual cases.',
          img: '/assets/landing/complaints_landing-card.jpg',
        },
        {
          title: 'Market research and insights',
          text: 'Move beyond one‑off reports and build ongoing text‑based KPIs for your clients or stakeholders.',
          img: '/assets/landing/mr_landing-card.jpg',
        },
      ],

      useCases: [
        {
          title: 'Track voice over time',
          text: 'Trend citizen, client, or employee themes month‑over‑month to see whether changes are working.',
          img: '/assets/landing/tracking_landing-card.jpg',
        },
        {
          title: 'Identify service improvement opportunities',
          text: 'Pinpoint the specific aspects of an experience that drive satisfaction, trust, or churn.',
          img: '/assets/landing/service_landing-card.jpg',
        },
        {
          title: 'Measure impact of changes',
          text: 'Compare feedback before and after a new policy, design, or campaign to quantify impact.',
          img: '/assets/landing/impact_landing-card.jpg',
        },
        {
          title: 'Detect emerging issues',
          text: 'Surface weak signals and minority voices that would be invisible in dashboards or averages.',
          img: '/assets/landing/detect_landing-card.jpg',
        },
        {
          title: 'Build custom models',
          text: 'Let analysts teach the system what matters; reuse those models across datasets and time.',
          img: '/assets/landing/models_landing-card.jpg',
        },
        {
          title: 'Produce audit‑ready reports',
          text: 'Generate defensible summaries and metrics that can be traced back to underlying comments.',
          img: '/assets/landing/audit_landing-card.jpg',
        },
      ],

      thoughtLeadership: [
        {
          tag: 'Concept',
          title: 'The Workslop Crisis',
          text: 'Why organizations are drowning in low‑quality AI output—and how validation‑first methods fix it.',
          link: '/library/feature/trusted_ai_series_workslop_crisis',
          img: '/assets/landing/workslop.jpg',
        },
        {
          tag: 'Methodology',
          title: 'Validation‑First Architecture',
          text: 'A governance‑first approach that combines deterministic methods, ML, and constrained GenAI.',
          link: '/library/feature/trusted_ai_series_validation_first_architecture',
          img: '/assets/landing/landing_validation-first.jpg',
        },
        {
          tag: 'Case',
          title: 'Airport CX and operational performance',
          text: 'How one airport used ResearchAI to connect passenger feedback to operational decisions.',
          link: '/library/feature/trusted_ai_series_validation_first_ai_in_airports',
          img: '/assets/landing/landing_airports.jpg',
        },
        {
          tag: 'Public sector',
          title: 'What Social Media Tells You That Surveys Never Will',
          text: 'How 1,200+ public reviews revealed the systemic failures behind a state motor vehicle agency.',
          link: '/library/feature/trusted_ai_series_validation_first_ai_at_dmv',
          img: '/assets/landing/landing_DMV.jpg',
        },
      ],
    };
  },

  created() {
    this.prefilledEmail = this.$route.query.email || '';
    this.inviteId = this.$route.query.c || null;

    if (this.inviteId && !this.inviteId.match(/^[0-9a-fA-F]{24}$/)) {
      this.$router.push({ name: 'login' });
    }
  },

  methods: {
    openRegisterModal() {
      this.isRegisterSmallExpanded = true;
    },

    async handleSmallRegister(user) {
      // redirect to register verification page if is_email_verified = false
      if (!user.is_email_verified) {
        this.$router.push({
          path: 'register/verify',
          query: {
            email: user.email,
            status: 'account_created',
          },
        });
      } else {
        // login user automatically
        await this.$pigeonline.login(
          this.$refs.register.$data['email'],
          this.$refs.register.$data['password'],
        );
        this.$router.push(
          {
            path: this.$route.query.redirect || 'account/profile',
          },
          () => this.$router.go(0),
        );
      }
    },
  },
};
</script>

<style scoped>
:root {
  --rai-blue: #1f7bbf;
  --rai-blue-light: #e6f3fb;
  --rai-blue-soft: #f4f8fc;
  --rai-navy: #0b2740;
  --rai-text: #1b2430;
  --rai-muted: #6b7280;
  --rai-border: #d1d5db;
  --rai-accent: #0ea5e9;
  --rai-bg-accent: #e0f2fe;
  --radius-lg: 16px;
  --radius-md: 10px;
  --radius-pill: 999px;
}

.landing {
  margin-bottom: -2rem;
  min-height: 100vh;
  background: #ffffff;
  color: var(--rai-text);
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.centered {
  margin-left: auto;
  margin-right: auto;
}

.flexbox {
  display: flex;
}

.mt-2rem {
  margin-top: 2rem;
}

.mt-3rem {
  margin-top: 3rem;
}

/* NAVBAR */

.nav {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(209, 213, 219, 0.6);
}

.nav__inner {
  max-width: 90%;
  margin: 0 auto;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.nav__brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav__links {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
}

.nav__link {
  color: var(--rai-muted);
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  transition:
    color 0.15s ease,
    background 0.15s ease;
}

.nav__link:hover {
  color: var(--rai-navy);
  background: var(--rai-blue-soft);
}

.nav__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logo-image {
  height: 2rem;
  margin-top: 0.5rem;
}

/* HERO */

.hero {
  background: radial-gradient(circle at top left, #e0f2fe 0, #ffffff 45%, #f4f8fc 100%);
  border-bottom: 1px solid var(--rai-blue-light);
  height: 40rem;
}

.hero__inner {
  max-width: 90%;
  padding: 0 1.25rem;
  display: flex;
  gap: 2.5rem;
  align-items: stretch;
  height: 100%;
  margin: auto;
}

.hero__content {
  flex: 1.2;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin: auto;
}

.hero__eyebrow {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--rai-accent);
  justify-content: center;
  display: flex;
  text-align: center;
}

.hero__title {
  font-size: clamp(2.1rem, 3vw, 2.6rem);
  line-height: 1.1;
  color: var(--rai-navy);
  justify-content: center;
  display: flex;
  text-align: center;
}

.hero__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.hero__visual {
  flex: 1;
  display: flex;
  align-items: stretch;
}

/* ARCH landing_card */

.landing_card {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 1.25rem 1.4rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.18s ease;
  overflow: hidden;
  position: relative;
}

.landing_card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: linear-gradient(90deg, #1f7bbf, #0ea5e9);
  opacity: 0.85;
}

.landing_card:hover {
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  border-color: #d0d7e2;
}

.landing_card__title {
  margin-bottom: 0.35rem;
  font-size: 1rem;
  font-weight: 600;
  color: #0b2740;
}

.landing_card__text {
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.45;
  max-width: 95%;
}

.landing_card__thin {
  max-width: 45% !important;
}

/* GENERIC BUTTONS / INPUTS */

.landing-btn {
  border-radius: var(--radius-pill);
  border: 1px solid transparent;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  white-space: nowrap;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.landing-btn--primary {
  background: var(--rai-blue);
  color: #ffffff;
  box-shadow: 0 10px 25px rgba(37, 99, 235, 0.25);
}

.landing-btn--primary:hover {
  background: #1d6ea9;
}

.landing-btn--outline {
  background: #ffffff;
  color: var(--rai-blue);
  border-color: var(--rai-blue);
}

.landing-btn--outline:hover {
  background: var(--rai-blue-soft);
}

.landing-btn--ghost {
  background: transparent;
  color: var(--rai-muted);
  border-color: transparent;
}

.landing-btn--ghost:hover {
  background: var(--rai-blue-soft);
  color: var(--rai-navy);
}

.input {
  border-radius: var(--radius-pill);
  border: 1px solid var(--rai-border);
  padding: 0.55rem 0.9rem;
  font-size: 0.9rem;
  width: 100%;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
  background: #ffffff;
}

.input:focus {
  border-color: var(--rai-blue);
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.15);
}

.input--textarea {
  border-radius: 12px;
}

.svg-icon {
  height: 2rem;
}

/* SECTIONS */

.section {
  padding: 3rem 1.25rem;
  border-bottom: 2px solid #eef2f7;
}

.section--light {
  background: var(--rai-blue-soft);
}

.section--accent {
  margin-top: 4rem;
  background: var(--rai-bg-accent);
}

.section__inner {
  max-width: 90%;
  margin: 0 auto;
}

.section__inner--split {
  display: flex;
  gap: 2.5rem;
  align-items: flex-start;
}

.section__inner--center {
  text-align: center;
}

@media (max-width: 640px) {
  .landing_card__thin {
    max-width: 100% !important;
  }

  .section__inner--center {
    max-width: 100% !important;
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.section__header {
  width: 100%;
  margin-bottom: 1.75rem;
}

.section__header h2 {
  font-size: 1.5rem;
  color: var(--rai-navy);
  margin-bottom: 0.5rem;
}

.section__header p {
  font-size: 0.95rem;
  color: var(--rai-muted);
}

.section__block {
  flex: 1;
}

/* Centered block */
.section__block--centered {
  text-align: center;
  max-width: 640px;
  margin: 0 auto;
}

/* Intro paragraph */
.section__intro {
  font-size: 1.1rem;
  color: #334155; /* slate-700 */
  margin-bottom: 1.75rem;
}

/* TAGS CONTAINER */
.problem-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

/* INDIVIDUAL TAG */
.problem-tag {
  display: inline-block;
  padding: 0.45rem 0.85rem;
  background: #f1f5f9; /* slate-100 */
  color: #475569; /* slate-600 */
  border: 1px solid #e2e8f0; /* slate-200 */
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
}

/* Centered highlight */
.section__highlight--centered {
  text-align: center;
  margin-top: 2rem;
}

.larger-font {
  font-size: 1.3rem;
}

.section__centred {
  display: flex;
  margin: auto;
  justify-content: center;
  text-align: center;
}

.section__highlight {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: var(--rai-navy);
  font-weight: 500;
}

.section__note {
  margin-top: 1.5rem;
  font-size: 0.85rem;
  color: var(--rai-muted);
}

.section__cta {
  margin-top: 1.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.section__cta .landing-btn {
  font-size: 0.9rem;
}

/* GRID */

.grid {
  display: grid;
  gap: 1.25rem;
}

.grid--cards {
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
}

.grid--cards--whoFor {
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: 2.5rem;
  max-width: 90%; /* controls total width */
  margin: 0 auto; /* centers the grid */
  width: 100%;
}
@media (max-width: 1024px) {
  .grid--cards--whoFor {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }
}

@media (max-width: 640px) {
  .grid--cards--whoFor {
    grid-template-columns: 1fr;
  }
}

.grid--three {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

/* CARD VARIANTS */

.landing_card__eyebrow {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--rai-accent);
  margin-bottom: 0.25rem;
}

.landing_card__stat {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--rai-navy);
  margin-bottom: 0.5rem;
}

.landing_card--stat {
  background: #4a5568;
  color: #e5e7eb;
  border-color: #1f2937;
}

.landing_card--stat .landing_card__eyebrow {
  color: #93c5fd;
}

.landing_card--stat .landing_card__text {
  color: #e5e7eb;
}

.landing_card--case {
  background: #ffffff;
}

.landing_card__link {
  display: inline-flex;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--rai-blue);
  text-decoration: none;
  flex-direction: column;
}

.landing_card__link:hover {
  text-decoration: underline;
}

/* QUOTE BLOCK */
.landing_quote {
  max-width: 720px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #f8fafc; /* very light slate/blue tint */
  border-radius: 6px;
  text-align: center;
}

.landing_quote__eyebrow {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b; /* slate-500 */
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.landing_quote__text {
  font-size: 1.125rem;
  line-height: 1.6;
  color: #1e293b; /* slate-800 */
  margin: 0;
  font-style: italic;
}

.landing_quote__attribution {
  margin-top: 1.25rem;
  font-size: 0.95rem;
  color: #475569; /* slate-600 */
  font-weight: 500;
}

/* LISTS */

.list {
  list-style: disc;
  padding-left: 1.1rem;
  font-size: 0.9rem;
  color: var(--rai-muted);
}

.list--tight li {
  margin-bottom: 0.25rem;
}

/* BADGE */

.badge {
  font-size: 1rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
  margin-bottom: 2rem;
}

/* WORKFLOW */

.workflow {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.workflow__step {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.workflow__index {
  height: 26px;
  width: 26px;
  border-radius: 999px;
  background: var(--rai-blue-soft);
  color: var(--rai-blue);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
}

.workflow__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--rai-text);
}

.workflow__text {
  font-size: 0.9rem;
  color: var(--rai-muted);
}

/* SIGNUP */

.signup {
  max-width: 640px;
  margin: 1.75rem auto 0;
  text-align: left;
}

.signup__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.signup__submit {
  width: 100%;
}

.landing_btn-gradient {
  background: linear-gradient(90deg, #1f7bbf, #0ea5e9);
  color: #ffffff;
  border: none;
  border-radius: 999px; /* pill shape */
  padding: 0.6rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  box-shadow: 0 4px 12px rgba(31, 123, 191, 0.25);
  height: 100%;
}

.landing_btn-gradient:hover {
  filter: brightness(0.92);
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(31, 123, 191, 0.35);
}

.landing_btn-outline {
  background: #ffffff;
  color: #1f7bbf;
  border: 2px solid #1f7bbf;
  border-radius: 999px;
  padding: 0.5rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  height: 100%;
  text-decoration: none;
}

.landing_btn-outline:hover {
  background: #e0f2fe; /* soft blue */
  transform: translateY(-2px);
  border-color: #0ea5e9;
}

.cursor-pointer {
  cursor: pointer;
}

/* ============================
   Workslop Section – Trust Panel
   ============================ */

.workslop-trust-panel {
  padding: 2rem 1.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  text-align: center;
  width: 80%;
  margin: auto;
  margin-top: 3rem;
}

.workslop-trust-text {
  font-size: 1rem;
  color: #0b2740;
  line-height: 1.55;
  max-width: 780px;
  margin: 0 auto 1.75rem;
}

.workslop-trust-logos {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.workslop-trust-logos img {
  height: 48px;
  width: auto;
  object-fit: contain;
  opacity: 0.9;
  transition: opacity 0.15s ease;
}

.workslop-trust-logos img:hover {
  opacity: 1;
}

.circle_logos {
  height: 6rem;
  margin: 0 2rem;
  width: 6rem;
}

/* PARTNER LOGO STRIP */
.partners {
  text-align: center;
  padding: 3rem 1.5rem 2rem;
}

.partners__heading {
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569; /* neutral slate */
  margin-bottom: 2rem;
  font-weight: 600;
}

.partners__logo-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2.5rem 3rem; /* row gap / column gap */
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}

/* LOGO STYLE */
.partners__logo {
  height: 50px; /* consistent height */
  width: auto;
  opacity: 0.75;
  filter: grayscale(100%);
  transition: opacity 0.2s ease;
}

.partners__logo:hover {
  opacity: 1;
}

/* ============================
   WHO-FOR CARDS (isolated)
   ============================ */

.who-card {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.18s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.who-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  border-color: #d0d7e2;
}

.who-card__image {
  height: 140px;
  object-fit: cover;
  display: block;
  margin: auto;
}

.who-card__image-long {
  height: 140px;
  object-fit: cover;
  display: block;
  margin: auto;
  width: 100%;
}

.who-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: #0b2740;
  padding: 1rem 1.1rem 0.25rem;
}

.who-card__text {
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.45;
  padding: 0 1.1rem 1.2rem;
}

/* RESPONSIVE */

@media (max-width: 960px) {
  .nav__links {
    display: none;
  }

  .hero__inner {
    flex-direction: column;
  }

  .section__inner--split {
    flex-direction: column;
  }
}

@media (max-width: 640px) {
  .nav__inner {
    padding-inline: 1rem;
  }

  .hero {
    padding-top: 0.5rem;
  }

  .hero__inner {
    padding-inline: 1rem;
    padding-bottom: 2.5rem;
  }

  .hero__inner--narrow {
    width: 60%;
  }

  .section {
    padding-inline: 1rem;
  }

  .signup__row {
    flex-direction: column;
  }
}

/* ============================
   MODERN FOOTER (ResearchAI)
   ============================ */

.footer-rai {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  padding: 3rem 1.5rem;
  margin-top: 4rem;
  font-family: system-ui, sans-serif;
}

.footer-rai__inner {
  max-width: 90%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  gap: 3rem;
  flex-wrap: wrap;
}

/* CONTACT SECTION */
.footer-rai__contact {
  flex: 1;
  min-width: 260px;
}

.footer-rai__heading {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0b2740;
  margin-bottom: 1rem;
}

.footer-rai__contact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  text-decoration: none;
}

.footer-rai__icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.footer-rai__label {
  font-size: 0.85rem;
  color: #1f7bbf;
  font-weight: 600;
}

.footer-rai__value {
  font-size: 0.95rem;
  color: #0b2740;
}

.footer-rai__value--link {
  text-decoration: underline;
  color: #1f7bbf;
}

/* POLICY SECTION */
.footer-rai__policies {
  flex: 1;
  min-width: 260px;
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.footer-rai__copyright {
  font-size: 0.9rem;
  color: #475569;
  margin-top: 1rem;
}

.footer-rai__policy-links {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.footer-rai__policy-link {
  font-size: 0.9rem;
  color: #1f7bbf;
  text-decoration: none;
}

.footer-rai__policy-link:hover {
  text-decoration: underline;
}

.footer-rai__divider {
  color: #94a3b8;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .footer-rai__policies {
    text-align: left;
  }

  .footer-rai__policy-links {
    justify-content: flex-start;
  }
}
</style>
